import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { keepPreviousData } from '@tanstack/react-query';

import { TraitGroup, Traits } from '@playq/octopus2-analytics';
import { AppEntityContentResponse } from '@playq/octopus2-apps';
import { GenericFailure } from '@playq/services-shared';
import { PermModule } from '@playq/octopus2-auth';

import { IUseQueryOptionsExtended, useEitherQuery } from '/api/service-hooks';
import { services2 } from '/api/services2';
import { appToolkit } from '/store';
import { useModulePermissions } from '/hooks';

import { hardcodedExperimentTraits, hardcodedGameEventTraits } from './hardcodedTraits';
import { appTraitsRetrieveKeys, useRetrieveSystemTraits } from './useRetrieveSystemTraits';

const emptyArray: [] = [];

const hardcodedTraits = { gameEvents: hardcodedGameEventTraits, experiments: hardcodedExperimentTraits };

export const useAppTraitsRetrieve = (
  version?: number,
  options?: IUseQueryOptionsExtended<AppEntityContentResponse | undefined, GenericFailure | Error>
) => {
  const appID = useSelector(appToolkit.selectors.appID);

  const { canRead } = useModulePermissions(PermModule.AnalyticsTraits);

  const enabled = options?.enabled !== false && canRead;

  const { groups: systemTraitsGroups, isLoading: isSystemTraitsLoading } = useRetrieveSystemTraits({
    staleTime: Infinity,
    enabled,
  });

  const res = useEitherQuery(
    appTraitsRetrieveKeys.concat(appID, version),
    () => {
      if (appID) {
        return services2.appTraitsService.retrieve(appID, version);
      }
    },
    {
      placeholderData: keepPreviousData,
      meta: {
        logOnError: true,
      },
      ...options,
      enabled,
    }
  );

  const traits = useMemo(() => {
    if (res.data !== undefined) {
      try {
        return new Traits(JSON.parse(res.data.entity.content));
      } catch (e) {
        console.error(e);
        return undefined;
      }
    }
  }, [res.data]);

  const traitsWithSystemTraits = useMemo(() => {
    if (isSystemTraitsLoading) {
      return emptyArray;
    }
    if (traits?.groups) {
      const mergedTraits: TraitGroup[] = [...systemTraitsGroups];
      mergedTraits.push(
        ...traits.groups.filter((group) => !systemTraitsGroups.some((systemTrait) => systemTrait.name === group.name))
      );
      return mergedTraits;
    }
    return systemTraitsGroups;
  }, [isSystemTraitsLoading, systemTraitsGroups, traits?.groups]);

  return {
    traits,
    traitsGroups: traitsWithSystemTraits,
    version: res.data?.entity.version,
    lastVersion: res.data?.lastVersion,
    hardcodedTraits,
    ...res,
  };
};
